export interface AbdmClinicalItem {
  readonly label: string;
  readonly value: string;
  readonly detail?: string;
}

export interface AbdmClinicalGroup {
  readonly title: string;
  readonly items: AbdmClinicalItem[];
}

export interface AbdmFhirView {
  readonly date?: string;
  readonly patient?: string;
  readonly patientDetails?: string;
  readonly practitioner?: string;
  readonly organization?: string;
  readonly groups: AbdmClinicalGroup[];
  readonly resourceCount: number;
}

type FhirObject = Record<string, unknown>;

export function presentFhirBundle(fhirJson: string): AbdmFhirView | undefined {
  try {
    const bundle = asObject(JSON.parse(fhirJson));
    return bundle ? buildView(bundle) : undefined;
  } catch {
    return undefined;
  }
}

export function formatFhirJson(fhirJson: string): string {
  try {
    return JSON.stringify(JSON.parse(fhirJson), null, 2);
  } catch {
    return fhirJson;
  }
}

function buildView(bundle: FhirObject): AbdmFhirView {
  const resources = readResources(bundle);
  const composition = findResource(resources, 'Composition');
  const patient = findResource(resources, 'Patient');
  return {
    date: readString(composition, 'date') || readString(bundle, 'timestamp'),
    patient: readPersonName(patient),
    patientDetails: buildPatientDetails(patient),
    practitioner: readPersonName(findResource(resources, 'Practitioner')),
    organization: readString(findResource(resources, 'Organization'), 'name'),
    groups: [buildNarratives(composition), buildMedications(resources)].filter(isGroup),
    resourceCount: resources.length
  };
}

function buildNarratives(composition?: FhirObject): AbdmClinicalGroup | undefined {
  const items = readArray(composition, 'section').map(asObject).filter(isObject)
    .map(section => ({
      label: readString(section, 'title') || 'Clinical note',
      value: readNarrative(section)
    }))
    .filter(item => item.value.length > 0);
  return items.length ? { title: 'Clinical details', items } : undefined;
}

function buildMedications(resources: FhirObject[]): AbdmClinicalGroup | undefined {
  const items = resources.filter(resource => readString(resource, 'resourceType') === 'MedicationRequest')
    .map(resource => ({
      label: readConcept(resource, 'medicationCodeableConcept') || 'Medication',
      value: readDosage(resource) || 'No dosage instructions provided',
      detail: readString(resource, 'status')
    }));
  return items.length ? { title: 'Medications', items } : undefined;
}

function readResources(bundle: FhirObject): FhirObject[] {
  return readArray(bundle, 'entry').map(asObject).filter(isObject)
    .map(entry => asObject(entry['resource'])).filter(isObject);
}

function findResource(resources: FhirObject[], type: string): FhirObject | undefined {
  return resources.find(resource => readString(resource, 'resourceType') === type);
}

function readPersonName(resource?: FhirObject): string | undefined {
  const name = asObject(readArray(resource, 'name')[0]);
  if (!name) return undefined;
  const text = readString(name, 'text');
  if (text) return text;
  return [...readStringArray(name, 'given'), readString(name, 'family')].filter(Boolean).join(' ') || undefined;
}

function buildPatientDetails(patient?: FhirObject): string | undefined {
  const gender = readString(patient, 'gender');
  const details = [gender ? gender[0].toUpperCase() + gender.slice(1) : undefined, readString(patient, 'birthDate')];
  return details.filter(Boolean).join(' ? ') || undefined;
}

function readNarrative(section: FhirObject): string {
  const narrative = asObject(section['text']);
  const markup = readString(narrative, 'div') || '';
  const document = new DOMParser().parseFromString(markup, 'text/html');
  return document.body.textContent?.replace(/\s+/g, ' ').trim() || '';
}

function readDosage(resource: FhirObject): string | undefined {
  const dosage = asObject(readArray(resource, 'dosageInstruction')[0]);
  if (!dosage) return undefined;
  const additional = asObject(readArray(dosage, 'additionalInstruction')[0]);
  return [readString(dosage, 'text'), readString(additional, 'text')].filter(Boolean).join(' ? ') || undefined;
}

function readConcept(resource: FhirObject, property: string): string | undefined {
  const concept = asObject(resource[property]);
  if (!concept) return undefined;
  const coding = asObject(readArray(concept, 'coding')[0]);
  return readString(concept, 'text') || readString(coding, 'display') || readString(coding, 'code');
}

function readString(object: FhirObject | undefined, property: string): string | undefined {
  const value = object?.[property];
  return typeof value === 'string' && value.trim() ? value.trim() : undefined;
}

function readArray(object: FhirObject | undefined, property: string): unknown[] {
  const value = object?.[property];
  return Array.isArray(value) ? value : [];
}

function readStringArray(object: FhirObject, property: string): string[] {
  return readArray(object, property).filter((value): value is string => typeof value === 'string');
}

function asObject(value: unknown): FhirObject | undefined {
  return value !== null && typeof value === 'object' && !Array.isArray(value) ? value as FhirObject : undefined;
}

function isObject(value: FhirObject | undefined): value is FhirObject {
  return value !== undefined;
}

function isGroup(value: AbdmClinicalGroup | undefined): value is AbdmClinicalGroup {
  return value !== undefined;
}
